import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { isTokenExpired, getTokenTimeRemaining, isTokenExpiringSoon } from '../../Helpers/jwt.utils';
import { setUnauthorizedCallback } from '../../Helpers/api-client';

interface User {
  token: string;
  user_id: string;
  uuid: string;
  email?: string;
  bar_no?: string;
}

interface AuthContextType {
  user: User | undefined;
  setUser: (user: User) => void;
  logout: () => void;
  isAuthenticated: boolean;
  isTokenValid: boolean;
  checkTokenValidity: () => boolean;
}

// Intervalo para verificar expiración del token (cada 30 segundos)
const TOKEN_CHECK_INTERVAL = 30 * 1000;

// Función para obtener el usuario inicial del localStorage de forma sincrónica
const getInitialUser = (): User | undefined => {
  const token = localStorage.getItem('token');
  const user_id = localStorage.getItem('user_id');
  const uuid = localStorage.getItem('uuid');
  
  // Solo retornar un objeto si hay un token válido y no está expirado
  if (token && !isTokenExpired(token)) {
    return { token, user_id: user_id || '', uuid: uuid || '' };
  }
  
  // Si el token existe pero está expirado, limpiarlo
  if (token) {
    console.log('[Auth] Token expirado encontrado en localStorage, limpiando...');
    localStorage.removeItem('token');
    localStorage.removeItem('user_id');
    localStorage.removeItem('uuid');
    localStorage.removeItem('user');
    localStorage.removeItem('bar_no');
  }
  
  return undefined;
};

const LoginContext = createContext<AuthContextType>({
  user: undefined,
  setUser: () => {},
  logout: () => {},
  isAuthenticated: false,
  isTokenValid: false,
  checkTokenValidity: () => false,
});

export const useLogin = () => {
  return useContext(LoginContext);
};

export const LoginProvider = ({ children }: { children: React.ReactNode }) => {
  // Inicializar el estado de forma sincrónica para evitar re-renders que limpian los inputs
  const [user, saveUser] = useState<User | undefined>(getInitialUser);

  // Función de logout centralizada
  const logout = useCallback(() => {
    console.log('[Auth] Cerrando sesión...');
    localStorage.removeItem('token');
    localStorage.removeItem('user_id');
    localStorage.removeItem('uuid');
    localStorage.removeItem('user');
    localStorage.removeItem('bar_no');
    saveUser(undefined);
  }, []);

  // Verificar si el token actual es válido
  const checkTokenValidity = useCallback((): boolean => {
    if (!user?.token) return false;
    return !isTokenExpired(user.token);
  }, [user?.token]);

  // Función para establecer el usuario
  const setUser = useCallback((newUser: User) => {
    if (!newUser?.token) {
      console.error('[Auth] Intento de establecer usuario sin token');
      return;
    }
    
    // Verificar que el token no esté expirado antes de guardarlo
    if (isTokenExpired(newUser.token)) {
      console.error('[Auth] Intento de establecer usuario con token expirado');
      logout();
      return;
    }
    
    localStorage.setItem('token', newUser.token);
    localStorage.setItem('user_id', newUser.user_id || '1');
    localStorage.setItem('user', JSON.stringify(newUser));
    localStorage.setItem('uuid', newUser.uuid || '');
    if (newUser.bar_no) {
      localStorage.setItem('bar_no', newUser.bar_no);
    }
    
    const userToSave = {
      ...newUser,
      user_id: newUser.user_id || '1'
    };

    saveUser(userToSave);
    console.log('[Auth] Usuario autenticado correctamente');
  }, [logout]);

  // Registrar callback para manejar errores 401 del API client
  useEffect(() => {
    setUnauthorizedCallback(() => {
      console.log('[Auth] Sesión expirada detectada por API client');
      logout();
    });
    
    return () => {
      setUnauthorizedCallback(() => {});
    };
  }, [logout]);

  // Verificación periódica de expiración del token
  useEffect(() => {
    if (!user?.token) return;

    const checkToken = () => {
      if (isTokenExpired(user.token)) {
        console.log('[Auth] Token expirado detectado en verificación periódica');
        logout();
        return;
      }
      
      // Advertir si el token está próximo a expirar (menos de 5 minutos)
      if (isTokenExpiringSoon(user.token, 5)) {
        const remaining = getTokenTimeRemaining(user.token);
        const minutes = Math.floor(remaining / 60000);
        console.log(`[Auth] Token expira en aproximadamente ${minutes} minutos`);
      }
    };

    // Verificar inmediatamente
    checkToken();

    // Configurar verificación periódica
    const intervalId = setInterval(checkToken, TOKEN_CHECK_INTERVAL);

    return () => {
      clearInterval(intervalId);
    };
  }, [user?.token, logout]);

  // Verificar al volver a la pestaña (cuando el usuario vuelve después de estar inactivo)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user?.token) {
        if (isTokenExpired(user.token)) {
          console.log('[Auth] Token expirado detectado al volver a la pestaña');
          logout();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user?.token, logout]);

  // Valores memoizados para evitar re-renders innecesarios
  const isAuthenticated = useMemo(() => !!user?.token, [user?.token]);
  const isTokenValid = useMemo(() => {
    return user?.token ? !isTokenExpired(user.token) : false;
  }, [user?.token]);

  const contextValue = useMemo(() => ({
    user,
    setUser,
    logout,
    isAuthenticated,
    isTokenValid,
    checkTokenValidity,
  }), [user, setUser, logout, isAuthenticated, isTokenValid, checkTokenValidity]);

  return (
    <LoginContext.Provider value={contextValue}>
      {children}
    </LoginContext.Provider>
  );
};
